import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Card,
  Table,
  Space,
  Button,
  Tag,
  Modal,
  message,
  Select,
  Input,
  Statistic,
  Tabs,
  Form,
  Switch,
  Row,
  Col,
  Alert
} from 'antd';
import {
  ReloadOutlined,
  WalletOutlined,
  SettingOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  SyncOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const FinancialTransactions = () => {
  const [activeTab, setActiveTab] = useState('deposits');
  const [transactions, setTransactions] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [assets, setAssets] = useState([]);
  const [masterWallets, setMasterWallets] = useState([]);
  const [monitoring, setMonitoring] = useState({ logs: [], summary: null });
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [assetModalVisible, setAssetModalVisible] = useState(false);
  const [masterModalVisible, setMasterModalVisible] = useState(false);
  const [assetForm] = Form.useForm();
  const [masterForm] = Form.useForm();

  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    pendingWithdrawals: 0,
    pendingDeposits: 0
  });

  // Modal Retrait
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [withdrawalForm] = Form.useForm();

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/transactions/stats');
      if (res.success && res.data) {
        setStats({
          totalDeposits: res.data.total_deposits || 0,
          totalWithdrawals: res.data.total_withdrawals || 0,
          pendingWithdrawals: res.data.pending_withdrawals || 0,
          pendingDeposits: res.data.pending_deposits || 0
        });
      }
    } catch (e) { /* silent */ }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('type', typeFilter);
      if (search) params.append('search', search);
      const res = await api.get(`/admin/transactions?${params.toString()}`);
      if (res.success) setTransactions(res.data?.transactions || []);
    } catch (e) {
      message.error('Erreur chargement transactions');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search]);

  const loadDeposits = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/deposits');
      if (res.success) setDeposits(res.data || []);
    } catch (e) {
      message.error('Erreur chargement dépôts');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/withdrawals');
      if (res.success) setWithdrawals(res.data || []);
    } catch (e) {
      message.error('Erreur chargement retraits');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/assets');
      if (res.success) setAssets(res.data || []);
    } catch (e) {
      message.error('Erreur chargement actifs');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMasterWallets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/master-wallets');
      if (res.success) setMasterWallets(res.data || []);
    } catch (e) {
      message.error('Erreur chargement wallets master');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMonitoring = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/deposits/admin/monitoring');
      if (res.success) setMonitoring(res.data);
    } catch (e) {
      // silencieux si la table n'existe pas encore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'transactions') loadTransactions();
    else if (activeTab === 'deposits') loadDeposits();
    else if (activeTab === 'withdrawals') loadWithdrawals();
    else if (activeTab === 'assets') loadAssets();
    else if (activeTab === 'master') loadMasterWallets();
    else if (activeTab === 'monitoring') loadMonitoring();
  }, [activeTab, loadTransactions, loadDeposits, loadWithdrawals, loadAssets, loadMasterWallets, loadMonitoring]);

  const handleForcePoll = async () => {
    try {
      setPolling(true);
      const res = await api.post('/deposits/admin/poll');
      if (res.success) {
        const { depositsFound, depositsCredited } = res.data;
        message.success(`Polling terminé : ${depositsFound} trouvé(s), ${depositsCredited} crédité(s)`);
        loadMonitoring();
        loadDeposits();
        loadStats();
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Erreur lors du polling');
    } finally {
      setPolling(false);
    }
  };

  const handleConfirmDeposit = async (id) => {
    try {
      const res = await api.put(`/admin/deposits/${id}/confirm`);
      if (res.success) {
        message.success('Dépôt crédité avec succès');
        loadDeposits();
        loadStats();
      }
    } catch (e) {
      message.error('Erreur lors de la confirmation');
    }
  };

  const handleProcessWithdrawal = async (values) => {
    try {
      const { id, action, tx_hash, notes } = values;
      const res = await api.put(`/admin/withdrawals/${id}/process`, { action, tx_hash, notes });
      if (res.success) {
        message.success(action === 'approve' ? 'Retrait approuvé avec TXID' : 'Retrait rejeté');
        setWithdrawalModalVisible(false);
        withdrawalForm.resetFields();
        loadWithdrawals();
        loadStats();
      }
    } catch (e) {
      message.error('Erreur lors du traitement');
    }
  };

  const handleUpdateAsset = async (values) => {
    try {
      const res = await api.put(`/admin/assets/${values.id}`, values);
      if (res.success) {
        message.success('Actif mis à jour');
        setAssetModalVisible(false);
        loadAssets();
      }
    } catch (e) {
      message.error('Erreur mise à jour actif');
    }
  };

  const handleUpdateMaster = async (values) => {
    try {
      const res = await api.put(`/admin/master-wallets/${values.asset}`, values);
      if (res.success) {
        message.success('Master wallet mis à jour');
        setMasterModalVisible(false);
        loadMasterWallets();
      }
    } catch (e) {
      message.error('Erreur mise à jour master wallet');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const depositColumns = [
    {
      title: 'Utilisateur',
      key: 'user',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.username || '—'}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.email || r.user_id}</div>
        </div>
      )
    },
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (v) => <Tag color="blue">{v}</Tag> },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (a) => <strong style={{ color: '#3f8600' }}>{parseFloat(a || 0).toFixed(8)}</strong>
    },
    { title: 'Réseau', dataIndex: 'chain', key: 'chain' },
    { title: 'Adresse', dataIndex: 'to_address', key: 'to_address', ellipsis: true },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (s) => (
        <Tag color={s === 'credited' ? 'green' : s === 'pending' ? 'orange' : 'default'}>
          {s === 'credited' ? 'Crédité' : s === 'pending' ? 'En attente' : s}
        </Tag>
      )
    },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: formatDate },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) =>
        r.status === 'pending' ? (
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleConfirmDeposit(r.id)}
          >
            Confirmer & Créditer
          </Button>
        ) : (
          <Tag color="green">Traité</Tag>
        )
    }
  ];

  const withdrawalColumns = [
    {
      title: 'Utilisateur',
      key: 'user',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.username || '—'}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.email || r.user_id}</div>
        </div>
      )
    },
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (v) => <Tag color="orange">{v}</Tag> },
    {
      title: 'Montant Net',
      dataIndex: 'net_amount',
      key: 'net_amount',
      render: (a) => <strong style={{ color: '#cf1322' }}>{parseFloat(a || 0).toFixed(8)}</strong>
    },
    { title: 'Adresse Dest.', dataIndex: 'to_address', key: 'to_address', ellipsis: true },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (s) => (
        <Tag color={s === 'sent' ? 'green' : s === 'requested' ? 'orange' : 'red'}>
          {s === 'sent' ? 'Envoyé' : s === 'requested' ? 'En attente' : s === 'rejected' ? 'Rejeté' : s}
        </Tag>
      )
    },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: formatDate },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) =>
        r.status === 'requested' ? (
          <Space>
            <Button
              type="primary"
              size="small"
              className="glass-button"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                setSelectedWithdrawal(r);
                withdrawalForm.setFieldsValue({ id: r.id, action: 'approve' });
                setWithdrawalModalVisible(true);
              }}
            >
              Traiter
            </Button>
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Rejeter le retrait ?',
                  content: 'Les fonds seront déverrouillés et rendus à l\'utilisateur.',
                  okText: 'Confirmer Rejet',
                  okType: 'danger',
                  onOk: () => handleProcessWithdrawal({ id: r.id, action: 'reject' })
                });
              }}
            >
              Rejeter
            </Button>
          </Space>
        ) : (
          <Tag color={r.status === 'sent' ? 'green' : 'red'}>
            {r.status === 'sent' ? 'Traité' : 'Terminé'}
          </Tag>
        )
    }
  ];

  const transactionColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: 'Utilisateur',
      key: 'user',
      render: (_, r) => r.user?.username || r.username || r.user_name || '—'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (t) => {
        const map = {
          deposit: { color: 'blue', label: 'Dépôt' },
          withdrawal: { color: 'orange', label: 'Retrait' },
          commission: { color: 'gold', label: 'Commission' },
          transfer: { color: 'cyan', label: 'Transfert' },
          admin_adjustment: { color: 'purple', label: 'Ajustement Admin' },
          investment: { color: 'geekblue', label: 'Investissement' },
          trade: { color: 'magenta', label: 'Trade' }
        };
        const c = map[t] || { color: 'default', label: t };
        return <Tag color={c.color}>{c.label}</Tag>;
      }
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (a, r) => (
        <span style={{ color: (r.change_amount > 0 || r.amount > 0) ? '#3f8600' : '#cf1322', fontWeight: 'bold' }}>
          {parseFloat(a || 0).toFixed(4)} {r.asset || r.symbol || ''}
        </span>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const map = {
          pending: { color: 'orange', label: 'En attente' },
          approved: { color: 'green', label: 'Approuvé' },
          completed: { color: 'green', label: 'Complété' },
          rejected: { color: 'red', label: 'Rejeté' }
        };
        const c = map[s] || { color: 'default', label: s };
        return <Tag color={c.color}>{c.label}</Tag>;
      }
    },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: formatDate }
  ];

  const assetColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nom / Symbole', dataIndex: 'name', key: 'name', render: (v, r) => <strong>{r.symbol || v}</strong> },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? 'Actif' : 'Inactif'}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Button
          size="small"
          icon={<SettingOutlined />}
          onClick={() => {
            assetForm.setFieldsValue(r);
            setAssetModalVisible(true);
          }}
        >
          Gérer
        </Button>
      )
    }
  ];

  const masterColumns = [
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Réseau', dataIndex: 'chain', key: 'chain' },
    { title: 'Adresse Master', dataIndex: 'address', key: 'address', ellipsis: true },
    {
      title: 'Solde (Agrégé)',
      dataIndex: 'balance',
      key: 'balance',
      render: (b) => <strong>{parseFloat(b || 0).toFixed(6)}</strong>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Button
          size="small"
          icon={<WalletOutlined />}
          onClick={() => {
            masterForm.setFieldsValue(r);
            setMasterModalVisible(true);
          }}
        >
          Ajuster
        </Button>
      )
    }
  ];

  const tabItems = [
    {
      key: 'deposits',
      label: (
        <span>
          <ArrowDownOutlined style={{ color: '#3f8600' }} /> Dépôts Clients
        </span>
      ),
      children: (
        <Card>
          <Alert
            message="Validation des dépôts"
            description="Vérifiez la transaction sur la blockchain, puis cliquez sur 'Confirmer & Créditer' pour ajouter les fonds au compte de l'utilisateur."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={deposits}
            columns={depositColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: 'Aucun dépôt trouvé' }}
          />
        </Card>
      )
    },
    {
      key: 'withdrawals',
      label: (
        <span>
          <ArrowUpOutlined style={{ color: '#cf1322' }} /> Retraits Clients
        </span>
      ),
      children: (
        <Card>
          <Alert
            message="Traitement des retraits"
            description="Effectuez d'abord le virement depuis votre wallet admin vers l'adresse de destination, puis cliquez 'Approuver'."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={withdrawals}
            columns={withdrawalColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: 'Aucune demande de retrait' }}
          />
        </Card>
      )
    },
    {
      key: 'transactions',
      label: (
        <span>
          <FilterOutlined /> Historique Global
        </span>
      ),
      children: (
        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              placeholder="Filtrer par type"
              style={{ width: 160 }}
              allowClear
              onChange={(v) => setTypeFilter(v || '')}
            >
              <Option value="deposit">Dépôts</Option>
              <Option value="withdrawal">Retraits</Option>
              <Option value="commission">Commissions</Option>
              <Option value="transfer">Transferts</Option>
              <Option value="admin_adjustment">Ajustements</Option>
              <Option value="investment">Investissements</Option>
            </Select>
            <Input
              placeholder="Rechercher utilisateur..."
              style={{ width: 220 }}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={loadTransactions}
            />
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadTransactions}>
              Actualiser
            </Button>
          </Space>
          <Table
            dataSource={transactions}
            columns={transactionColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: 'Aucune transaction trouvée' }}
          />
        </Card>
      )
    },
    {
      key: 'assets',
      label: (
        <span>
          <SettingOutlined /> Actifs Crypto
        </span>
      ),
      children: (
        <Card>
          <Table
            dataSource={assets}
            columns={assetColumns}
            rowKey="id"
            loading={loading}
            pagination={false}
            locale={{ emptyText: 'Aucun actif configuré' }}
          />
        </Card>
      )
    },
    {
      key: 'master',
      label: (
        <span>
          <WalletOutlined /> Wallets Master
        </span>
      ),
      children: (
        <Card>
          <Alert
            message="Wallets Master (Agrégation)"
            description="Ces wallets représentent les adresses centrales de la plateforme. Chaque dépôt confirmé incrémente le solde agrégé correspondant."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              type="primary" 
              icon={<WalletOutlined />} 
              onClick={() => {
                masterForm.resetFields();
                setMasterModalVisible(true);
              }}
            >
              Ajouter un Wallet Master
            </Button>
          </div>
          <Table
            dataSource={masterWallets}
            columns={masterColumns}
            rowKey="asset"
            loading={loading}
            pagination={false}
            locale={{ emptyText: 'Aucun wallet master configuré' }}
          />
        </Card>
      )
    },
    {
      key: 'monitoring',
      label: (
        <span>
          <SyncOutlined spin={polling} /> Monitoring Binance
        </span>
      ),
      children: (
        <Card>
          <Alert
            message="Surveillance automatique des dépôts Binance"
            description="Le système interroge automatiquement votre compte Binance toutes les 2 minutes et crédite les utilisateurs dès qu'un dépôt est confirmé."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {/* Résumé */}
          {monitoring.summary && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic title="Checks Total" value={monitoring.summary.total_checks || 0} />
              </Col>
              <Col span={6}>
                <Statistic title="Dépôts Détectés" value={monitoring.summary.total_found || 0} precision={2} valueStyle={{ color: '#1890ff' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Dépôts Crédités" value={monitoring.summary.total_credited || 0} precision={2} valueStyle={{ color: '#3f8600' }} />
              </Col>
              <Col span={6}>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Dernier Check</div>
                  <div style={{ fontWeight: 600 }}>
                    {monitoring.summary.last_check
                      ? new Date(monitoring.summary.last_check).toLocaleString('fr-FR')
                      : 'Jamais'}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Bouton Force Poll */}
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={polling}
              onClick={handleForcePoll}
              size="large"
            >
              Forcer un polling maintenant
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadMonitoring} loading={loading}>
              Rafraîchir
            </Button>
          </Space>

          {/* Historique des checks */}
          <Table
            dataSource={monitoring.logs}
            rowKey="id"
            loading={loading}
            size="small"
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: 'Aucun log de monitoring (le poller n\'a pas encore tourné)' }}
            columns={[
              {
                title: 'Date/Heure',
                dataIndex: 'checked_at',
                key: 'checked_at',
                render: (d) => new Date(d).toLocaleString('fr-FR')
              },
              {
                title: 'Dépôts Trouvés',
                dataIndex: 'deposits_found',
                key: 'deposits_found',
                render: (v) => <Tag color={v > 0 ? 'blue' : 'default'}>{v}</Tag>
              },
              {
                title: 'Dépôts Crédités',
                dataIndex: 'deposits_credited',
                key: 'deposits_credited',
                render: (v) => <Tag color={v > 0 ? 'green' : 'default'}>{v}</Tag>
              },
              {
                title: 'Statut',
                dataIndex: 'error_message',
                key: 'error_message',
                render: (err) => err
                  ? <Tag color="red">Erreur: {err.substring(0, 50)}</Tag>
                  : <Tag color="green">OK</Tag>
              }
            ]}
          />
        </Card>
      )
    }
  ];

  return (
    <div className="admin-transactions-inner animate-fade-in">
      <div className="glass-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} className="admin-page-title">Gestion Financière</Title>
            <Text className="admin-page-subtitle">Suivi des flux, validation des dépôts et traitement des retraits.</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={loadStats} className="glass-button">Actualiser Global</Button>
        </div>
      </div>

      {/* Statistiques */}
      <Row gutter={[20, 20]} style={{ marginBottom: 32 }}>
        {[
          { title: "Dépôts Totaux", value: stats.totalDeposits, color: "green", prefix: "$" },
          { title: "Retraits Totaux", value: stats.totalWithdrawals, color: "red", prefix: "$" },
          { title: "Dépôts En Attente", value: stats.pendingDeposits, color: "blue" },
          { title: "Retraits En Attente", value: stats.pendingWithdrawals, color: "orange" }
        ].map((s, i) => (
          <Col xs={12} sm={6} key={i}>
            <Card className="glass-card">
              <Statistic
                title={<span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{s.title}</span>}
                value={s.value}
                precision={s.prefix ? 2 : 0}
                prefix={s.prefix}
                valueStyle={{ color: s.color === 'green' ? '#4ade80' : s.color === 'red' ? '#f87171' : s.color === 'blue' ? '#60a5fa' : '#fb923c', fontWeight: 900 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Onglets */}
      <div className="glass-card" style={{ padding: '2px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={tabItems}
          className="admin-tabs-custom"
        />
      </div>

      {/* Modal Retrait TXID */}
      <Modal
        title="Approuver le Retrait"
        open={withdrawalModalVisible}
        onCancel={() => setWithdrawalModalVisible(false)}
        onOk={() => withdrawalForm.submit()}
        okText="Confirmer & Envoyer"
        className="glass-modal"
      >
        <Form form={withdrawalForm} onFinish={handleProcessWithdrawal} layout="vertical">
          <Form.Item name="id" hidden><Input /></Form.Item>
          <Form.Item name="action" hidden><Input /></Form.Item>
          
          <Alert 
            message="Action Critique" 
            description={`Vous vous apprêtez à marquer le retrait de ${selectedWithdrawal?.net_amount} ${selectedWithdrawal?.asset} comme envoyé.`} 
            type="warning" 
            showIcon 
            style={{ marginBottom: 20 }}
          />

          <div className="mb-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
            <div className="text-xs text-slate-500 uppercase font-bold mb-1">Adresse de destination</div>
            <div className="font-mono text-blue-400 break-all">{selectedWithdrawal?.to_address}</div>
          </div>

          <Form.Item 
            name="tx_hash" 
            label="Hash de Transaction (TXID)" 
            rules={[{ required: true, message: 'Le TXID est obligatoire pour prouver l\'envoi' }]}
            help="Collez ici le hash fourni par votre wallet ou Binance après l'envoi."
          >
            <Input placeholder="0x... ou hash de transaction" className="glass-input" />
          </Form.Item>

          <Form.Item name="notes" label="Note interne (optionnel)">
            <Input.TextArea placeholder="Ex: Envoyé via Ledger..." className="glass-input" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modals Existants ... (Asset & Master) */}
      <Modal
        title="Gérer l'Actif"
        open={assetModalVisible}
        onCancel={() => setAssetModalVisible(false)}
        onOk={() => assetForm.submit()}
        okText="Enregistrer"
      >
        <Form form={assetForm} onFinish={handleUpdateAsset} layout="vertical">
          <Form.Item name="id" hidden><Input /></Form.Item>
          <Form.Item name="symbol" label="Symbole"><Input disabled /></Form.Item>
          <Form.Item name="name" label="Nom complet"><Input /></Form.Item>
          <Form.Item name="is_active" label="Actif sur la plateforme" valuePropName="checked">
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Ajuster le Master Wallet"
        open={masterModalVisible}
        onCancel={() => setMasterModalVisible(false)}
        onOk={() => masterForm.submit()}
        okText="Enregistrer"
      >
        <Form form={masterForm} onFinish={handleUpdateMaster} layout="vertical">
          <Form.Item name="asset" label="Asset" rules={[{ required: true }]}>
            <Input placeholder="Ex: USDT, BTC..." />
          </Form.Item>
          <Form.Item name="chain" label="Réseau" rules={[{ required: true }]}>
            <Input placeholder="Ex: ERC20, BEP20, MAIN..." />
          </Form.Item>
          <Form.Item name="address" label="Adresse Blockchain Master" rules={[{ required: true }]}>
            <Input className="font-mono" />
          </Form.Item>
          <Form.Item name="balance" label="Solde Réel (pour affichage)">
            <Input type="number" step="0.000001" />
          </Form.Item>
        </Form>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .admin-tabs-custom .ant-tabs-nav { margin-bottom: 0 !important; }
        .admin-tabs-custom .ant-tabs-tab { 
          background: transparent !important; 
          border: none !important; 
          padding: 12px 24px !important;
          color: #94a3b8 !important;
        }
        .admin-tabs-custom .ant-tabs-tab-active { 
          background: rgba(99, 102, 241, 0.1) !important; 
        }
        .admin-tabs-custom .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #fff !important;
          font-weight: 700 !important;
        }
      `}} />
    </div>
  );
};

export default FinancialTransactions;